import { flatten, keyBy, pick, values } from 'lodash';
import { TopicServiceModel } from '@app/origami-api/models/topic-service.model';
import { buildTopicModuleInfo, TopicModule, TopicModuleInfo } from '@app/origami-api/constants/topic-type-names';
import { NumberHash } from '@app/shared/utils/utils-types';

export class CustomTopicTypes {
  private _items: TopicServiceModel.TopicType[] = [];
  private _byModule: Partial<Record<TopicModule, Record<number, TopicModuleInfo>>> = {};
  private _formNameById: NumberHash<string> = {};

  get items(): TopicServiceModel.TopicType[] {
    return this._items;
  }

  get ids(): number[] {
    return this._items.map((t) => t.TopicTypeId);
  }

  get byId(): Record<number, TopicServiceModel.TopicType> {
    return keyBy(this._items, 'TopicTypeId');
  }

  get byModule(): Partial<Record<TopicModule, Record<number, TopicModuleInfo>>> {
    return this._byModule;
  }

  getInfoForModules(modules: TopicModule[]): TopicModuleInfo[] {
    return flatten(modules.map((m) => values(this._byModule[m] ?? {})));
  }

  /**
   * The display name of a custom form is stored on the form itself (Form.FormName) and can be renamed
   * in the form settings, so it is the title to show - not the topic type name it was created with.
   */
  getFormName(topicTypeId: number): string | undefined {
    return this._formNameById[topicTypeId];
  }

  setFormNames(formNameById: NumberHash<string>): void {
    this._formNameById = pick(formNameById ?? {}, this.ids);
  }

  set(items: TopicServiceModel.TopicType[]): void {
    this._items = items ?? [];
    this._byModule = this.buildByModule(this._items);
    // drop the names of topic types that no longer exist; the rest are refreshed by setFormNames()
    this._formNameById = pick(this._formNameById, this.ids);
  }

  clear(): void {
    this._items = [];
    this._byModule = {};
    this._formNameById = {};
  }

  private buildByModule(
    items: TopicServiceModel.TopicType[],
  ): Partial<Record<TopicModule, Record<number, TopicModuleInfo>>> {
    const result: Partial<Record<TopicModule, Record<number, TopicModuleInfo>>> = {};
    items.forEach((topic) => {
      topic.Modules?.forEach((m) => {
        const bucket = (result[m.Module] ??= {});
        bucket[topic.TopicTypeId] = buildTopicModuleInfo(topic, m);
      });
    });
    return result;
  }
}
